import React from 'react';
import { Skeleton, SparkIcon, TagColored, TooltipTrigger } from '@spark';
import { TaskType, isGenerativeTaskLoose } from '@/shared/domain/taskTypes';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import PlaygroundTrajectory from './PlaygroundTrajectory';

const CheckIcon = () => (
    <svg width="8" height="6" viewBox="0 0 8 6" fill="none" aria-hidden="true">
        <path
            d="M1 3.2 2.9 5 7 1"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const BenchmarkCheckbox = ({ checked, onToggle }) => (
    <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        className={[
            'pg-benchmark-checkbox',
            checked ? 'pg-benchmark-checkbox--checked' : '',
        ].filter(Boolean).join(' ')}
        onClick={onToggle}
    >
        {checked ? <CheckIcon /> : null}
    </button>
);

const isNerTask = (taskType) => {
    const tt = (taskType || '').toLowerCase();
    return (
        tt === TaskType.NER.toLowerCase() ||
        tt === TaskType.NER_WITH_RELATIONS.toLowerCase()
    );
};

const isClassificationTask = (taskType) =>
    (taskType || '').toLowerCase() === TaskType.classification.toLowerCase();

// Best-effort plain text for the parsed view of a completion, per task type.
export const parsedTextFor = (output, taskType) => {
    const parsed = output?.parsed;
    if (typeof parsed?.response === 'string') return parsed.response;
    if (isNerTask(taskType) && Array.isArray(parsed?.entities)) {
        return parsed.entities
            .map((e) => `${e?.text ?? e?.value ?? ''} — ${e?.label ?? e?.entity ?? ''}`)
            .join('\n');
    }
    if (Array.isArray(parsed)) return parsed.filter(Boolean).join(', ');
    if (typeof parsed === 'string') return parsed;
    return output?.rawText || '';
};

const rawTextFor = (output) => {
    if (typeof output?.rawText === 'string' && output.rawText) return output.rawText;
    const parsed = output?.parsed;
    if (parsed === undefined || parsed === null) return '';
    if (typeof parsed === 'string') return parsed;
    try {
        return JSON.stringify(parsed, null, 2);
    } catch (e) {
        return String(parsed);
    }
};

const entityContentOf = (entity) =>
    entity?.content ?? entity?.text ?? entity?.value ?? '';
const entityLabelOf = (entity) => entity?.label ?? entity?.entity ?? '';

/**
 * NER parsed view (Figma .Playground-NER-Data-Row): the input text rendered
 * inline with `.Playground-Entity-Highlight` spans (subtle bg + bold text per
 * label, 2px 4px, radius 4), followed by the `.Playground-Entity-Legend`
 * row of label chips with `×N` counts.
 */
const NerParsedContent = ({ output, inputText, allLabels }) => {
    const entities = Array.isArray(output?.parsed?.entities)
        ? output.parsed.entities.filter((e) => entityContentOf(e))
        : [];

    // Hovering an entity shows its label — same pattern as the evaluation
    // "Text Sample" modal (TaggedSample's light tooltip).
    const highlight = (entity, key) => (
        <TooltipTrigger key={key} text={entityLabelOf(entity)} variant="light">
            <span
                className={`pg-entity pg-entity--${getSparkTagVariant(entityLabelOf(entity), allLabels)}`}
            >
                {entityContentOf(entity)}
            </span>
        </TooltipTrigger>
    );

    // Interleave the source text with highlighted spans when start indexes
    // are available and consistent (including the empty-entities case, which
    // should still show the input with no highlights). Otherwise fall back
    // to highlights only — never the raw JSON, which belongs on the Raw view.
    let body = null;
    const indexed = entities
        .filter((e) => Number.isFinite(e?.start_index))
        .sort((a, b) => a.start_index - b.start_index);
    if (inputText && indexed.length === entities.length) {
        const parts = [];
        let cursor = 0;
        let valid = true;
        indexed.forEach((entity, idx) => {
            const start = entity.start_index;
            const end = start + entityContentOf(entity).length;
            if (start < cursor || end > inputText.length) {
                valid = false;
                return;
            }
            if (start > cursor) {
                parts.push(<span key={`t-${idx}`}>{inputText.slice(cursor, start)}</span>);
            }
            parts.push(highlight(entity, `e-${idx}`));
            cursor = end;
        });
        if (valid) {
            if (cursor < inputText.length) {
                parts.push(<span key="t-end">{inputText.slice(cursor)}</span>);
            }
            body = <div className="pg-ner-body">{parts}</div>;
        }
    }
    if (!body) {
        body =
            entities.length > 0 ? (
                <div className="pg-ner-body">
                    {entities.map((entity, idx) => highlight(entity, `e-${idx}`))}
                </div>
            ) : (
                <div className="pg-ner-body">{inputText || ''}</div>
            );
    }

    const counts = new Map();
    entities.forEach((entity) => {
        const label = entityLabelOf(entity);
        counts.set(label, (counts.get(label) || 0) + 1);
    });

    return (
        <div className="pg-ner">
            {body}
            {counts.size > 0 && (
                <div className="pg-entity-legend">
                    {[...counts.entries()].map(([label, count]) => (
                        <span className="pg-entity-legend__item" key={label}>
                            <TagColored
                                label={label}
                                variant={getSparkTagVariant(label, allLabels)}
                            />
                            <span className="pg-entity-legend__count">{`×${count}`}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const ParsedContent = ({ output, taskType, inputText, allLabels }) => {
    // An agentic run is judged on how it got there, so the parsed view is the
    // conversation. The transcript stays available under "Raw".
    if (Array.isArray(output?.trajectory) && output.trajectory.length > 0) {
        return <PlaygroundTrajectory trajectory={output.trajectory} />;
    }
    if (isNerTask(taskType) && Array.isArray(output?.parsed?.entities)) {
        return (
            <NerParsedContent
                output={output}
                inputText={inputText}
                allLabels={allLabels}
            />
        );
    }
    if (isClassificationTask(taskType)) {
        const parsed = output?.parsed;
        const labels = Array.isArray(parsed)
            ? parsed.filter(Boolean)
            : Array.isArray(parsed?.labels)
                ? parsed.labels.filter(Boolean)
                : [];
        if (labels.length > 0) {
            // Figma classification cell: tag row, then `Confidence: 0.92`
            // on its own line underneath.
            const named = labels.map((label) =>
                typeof label === 'string'
                    ? { name: label, score: null }
                    : { name: label?.label, score: Number.isFinite(label?.score) ? label.score : null }
            );
            const scored = named.filter((l) => l.score !== null);
            return (
                <div className="pg-classification">
                    <div className="pg-output-cell__labels">
                        {named.map(({ name }, idx) => (
                            <TagColored
                                key={`${name}-${idx}`}
                                label={String(name)}
                                variant={getSparkTagVariant(String(name), allLabels)}
                            />
                        ))}
                    </div>
                    {scored.length > 0 && (
                        <span className="pg-output-cell__confidence">
                            {`Confidence: ${scored
                                .map((l) => l.score.toFixed(2))
                                .join(' · ')}`}
                        </span>
                    )}
                </div>
            );
        }
    }
    return (
        <div className="pg-output-cell__text">{parsedTextFor(output, taskType)}</div>
    );
};

/**
 * One model output cell (Figma 1266:45667 variants):
 * - empty ("before"): sparkles icon + "Generated output will appear here"
 * - loading: skeleton paragraph
 * - parsed view (default): transparent surface, "Raw" link in the actions row
 * - raw view (Figma 1645:139742): neutral-200 surface, code-font raw output,
 *   "Inspect" (commercial models only) + "Parsed" links
 * - benchmark selection mode: 14px checkbox on the left of the actions row,
 *   selected cells get the active-border treatment, saveable-empty cells dim.
 */
const OutputCell = ({
    output,
    taskType,
    inputText,
    allLabels,
    isCommercial,
    onToggleView,
    onInspectLogic,
    benchmarkMode,
    benchmarkSelected,
    onToggleBenchmark,
}) => {
    const status = output?.status || 'idle';

    if (status === 'loading') {
        // Loading skeleton mirrors the done-cell layout (Figma 1573:76393):
        // short action line top-right, paragraph lines, tokens line bottom-left.
        return (
            <div className="pg-output-cell">
                <div className="pg-output-cell__skeleton" data-testid="pg-output-skeleton">
                    <div className="pg-output-cell__skeleton-actions">
                        <Skeleton shapeType="rectangle" width={48} height={15} />
                    </div>
                    <div className="pg-output-cell__skeleton-lines">
                        <Skeleton shapeType="rectangle" width="100%" height={15} />
                        <Skeleton shapeType="rectangle" width="100%" height={15} />
                        <Skeleton shapeType="rectangle" width="100%" height={15} />
                        <Skeleton shapeType="rectangle" width="55%" height={15} />
                    </div>
                    <Skeleton shapeType="rectangle" width={92} height={15} />
                </div>
            </div>
        );
    }

    if (status !== 'done') {
        return (
            <div className={[
                'pg-output-cell',
                benchmarkMode ? 'pg-output-cell--dimmed' : '',
            ].filter(Boolean).join(' ')}
            >
                <div className="pg-output-cell__empty">
                    <SparkIcon slug="sparkles-3" size="small" />
                    {status === 'error'
                        ? output?.errorMessage || 'Inference failed for this example'
                        : 'Generated output will appear here'}
                </div>
            </div>
        );
    }

    const view = output.view || 'parsed';
    const isRawView = view === 'raw';
    // "Inspect" opens the parser-config modal — raw view only, and only for
    // commercial models (SpecificAI outputs are parsed natively, no parser).
    const showInspect = isRawView && !!isCommercial && !!onInspectLogic;

    const cellClass = [
        'pg-output-cell',
        isRawView ? 'pg-output-cell--raw' : '',
        benchmarkMode && benchmarkSelected ? 'pg-output-cell--selected' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={cellClass}>
            <div
                className={[
                    'pg-output-cell__actions',
                    benchmarkMode ? 'pg-output-cell__actions--with-checkbox' : '',
                ].filter(Boolean).join(' ')}
            >
                {benchmarkMode && (
                    <div className="pg-output-cell__actions-left">
                        <BenchmarkCheckbox
                            checked={benchmarkSelected}
                            onToggle={onToggleBenchmark}
                        />
                    </div>
                )}
                <div className="pg-output-cell__actions-right">
                    {showInspect && (
                        <button type="button" className="pg-link" onClick={onInspectLogic}>
                            Inspect
                        </button>
                    )}
                    <button type="button" className="pg-link" onClick={onToggleView}>
                        {isRawView ? 'Parsed' : 'Raw'}
                    </button>
                </div>
            </div>
            {isRawView ? (
                <div className="pg-output-cell__text pg-output-cell__code">
                    {rawTextFor(output)}
                </div>
            ) : (
                <ParsedContent
                    output={output}
                    taskType={taskType}
                    inputText={inputText}
                    allLabels={allLabels}
                />
            )}
            {/* Figma shows the tokens footer on generative cells only —
                NER / classification cells end with the legend / confidence. */}
            {isGenerativeTaskLoose(taskType) && (
                <span className="pg-output-cell__tokens">
                    {`Tokens: ${output.tokensOut ?? 0} out`}
                </span>
            )}
        </div>
    );
};

export default OutputCell;
